import { z } from 'zod';

export const DEVICE_PRESETS = {
  'iphone-17-pro-max': { width: 1320, height: 2868 },
  'iphone-17-pro': { width: 1206, height: 2622 },
  'iphone-17-air': { width: 1260, height: 2736 },
  'iphone-17': { width: 1206, height: 2622 },
  'iphone-16-pro-max': { width: 1320, height: 2868 },
  'iphone-16-pro': { width: 1206, height: 2622 },
  'iphone-16-plus': { width: 1290, height: 2796 },
  'iphone-16': { width: 1179, height: 2556 },
  'iphone-15-pro-max': { width: 1290, height: 2796 },
  'iphone-15-pro': { width: 1179, height: 2556 },
  'iphone-15': { width: 1179, height: 2556 },
  'iphone-se': { width: 750, height: 1334 },
  'pixel-8-pro': { width: 1344, height: 2992 },
  'galaxy-s25-ultra': { width: 1440, height: 3120 },
  'ipad-pro-13': { width: 2064, height: 2752 },
  'desktop-1080p': { width: 1920, height: 1080 },
  'desktop-4k': { width: 3840, height: 2160 },
} as const;

export type DevicePreset = keyof typeof DEVICE_PRESETS;

export const COLOR_PRESETS = {
  dark: { background: '#18181b', filled: '#d4d4d8', empty: '#3f3f46', today: '#ff622e', text: '#d4d4d8' },
  light: { background: '#fafafa', filled: '#52525b', empty: '#d4d4d8', today: '#ff622e', text: '#52525b' },
  amber: { background: '#18181b', filled: '#fbbf24', empty: '#3f3f46', today: '#f97316', text: '#fbbf24' },
  rose: { background: '#18181b', filled: '#fb7185', empty: '#3f3f46', today: '#e11d48', text: '#fb7185' },
  blue: { background: '#18181b', filled: '#60a5fa', empty: '#3f3f46', today: '#3b82f6', text: '#60a5fa' },
  emerald: { background: '#18181b', filled: '#34d399', empty: '#3f3f46', today: '#10b981', text: '#34d399' },
  mono: { background: '#000000', filled: '#ffffff', empty: '#404040', today: '#c0c0c0', text: '#ffffff' },
  frost: { background: '#f8fafc', filled: '#94a3b8', empty: '#e2e8f0', today: '#3b82f6', text: '#64748b' },
  blush: { background: '#fff1f2', filled: '#fca5a5', empty: '#ffe4e6', today: '#f43f5e', text: '#e11d48' },
  sage: { background: '#f0fdf4', filled: '#86efac', empty: '#dcfce7', today: '#22c55e', text: '#16a34a' },
  lavender: { background: '#faf5ff', filled: '#d8b4fe', empty: '#f3e8ff', today: '#a855f7', text: '#9333ea' },
  four_sisters_blush: { background: "#fff1f2", filled: "#fca5a5", empty: "#ffe4e6", today: "#f43f5e", text: "#e11d48" },
  newborn_sage: { background: "#f0fdf4", filled: "#86efac", empty: "#dcfce7", today: "#22c55e", text: "#16a34a" },
  mushroom_taupe: { background: "#f5f5f4", filled: "#d6d3d1", empty: "#e7e5e4", today: "#a8a29e", text: "#78716c" },
  terracotta_nursery: { background: "#fff7ed", filled: "#fdba74", empty: "#ffedd5", today: "#f97316", text: "#c2410c" },
  cursor_light: { background: "#ffffff", filled: "#93c5fd", empty: "#eff6ff", today: "#3b82f6", text: "#1e3a8a" },
  github_light: { background: "#ffffff", filled: "#81a1c1", empty: "#f6f8fa", today: "#0366d6", text: "#24292e" },
  tokyo_night_light: { background: "#d5d6db", filled: "#9699a3", empty: "#e1e2e7", today: "#8c4351", text: "#343b58" },
  catppuccin_latte: { background: "#eff1f5", filled: "#bcc0cc", empty: "#e6e9ef", today: "#1e66f5", text: "#4c4f69" },
  staff_engineer_slate: { background: "#f8fafc", filled: "#cbd5e1", empty: "#f1f5f9", today: "#64748b", text: "#334155" },
  gcp_blue: { background: "#ffffff", filled: "#8ab4f8", empty: "#e8f0fe", today: "#1a73e8", text: "#1a73e8" },
  alisal_ranch: { background: "#fffbf2", filled: "#d4a373", empty: "#faedcd", today: "#a98467", text: "#8a5a44" },
  titanium: { background: "#f4f4f5", filled: "#a1a1aa", empty: "#e4e4e7", today: "#52525b", text: "#3f3f46" },
  piedmont_fog: { background: "#f1f5f9", filled: "#94a3b8", empty: "#e2e8f0", today: "#475569", text: "#334155" },
  dracula: { background: "#282a36", filled: "#bd93f9", empty: "#44475a", today: "#ff79c6", text: "#f8f8f2" },
  monokai: { background: "#272822", filled: "#a6e22e", empty: "#3e3d32", today: "#f92672", text: "#f8f8f2" },
  nord_dark: { background: "#2e3440", filled: "#88c0d0", empty: "#3b4252", today: "#bf616a", text: "#d8dee9" },
  one_dark: { background: "#282c34", filled: "#61afef", empty: "#3e4451", today: "#e06c75", text: "#abb2bf" },
  midnight_nursery: { background: "#0f172a", filled: "#818cf8", empty: "#1e293b", today: "#c084fc", text: "#94a3b8" },
  quiet_house: { background: "#171717", filled: "#a1a1aa", empty: "#262626", today: "#5eead4", text: "#d4d4d8" },
  cloud_console_dark: { background: "#202124", filled: "#8ab4f8", empty: "#3c4043", today: "#fbbc04", text: "#e8eaed" },
  precision_charcoal: { background: "#1c1917", filled: "#f43f5e", empty: "#292524", today: "#fb923c", text: "#e7e5e4" },
  soundcloud_dark: { background: "#111111", filled: "#ff5500", empty: "#333333", today: "#ff7700", text: "#cccccc" },
  cargo_night: { background: "#09090b", filled: "#f59e0b", empty: "#27272a", today: "#10b981", text: "#a1a1aa" }
} as const;

export type ColorPreset = keyof typeof COLOR_PRESETS;

const hexColor = z.string().regex(/^#[0-9a-fA-F]{6}$/, 'expected #RRGGBB hex color');
const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'expected YYYY-MM-DD');

const deviceSchema = z
  .object({
    preset: z.string().optional(),
    width: z.number().int().positive().optional(),
    height: z.number().int().positive().optional(),
  })
  .superRefine((d, ctx) => {
    if (d.preset && !(d.preset in DEVICE_PRESETS)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['preset'],
        message: `unknown preset; valid: ${Object.keys(DEVICE_PRESETS).join(', ')}`,
      });
    }
    if (!d.preset && (!d.width || !d.height)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'set device.preset or both width and height',
      });
    }
  })
  .transform((d) => {
    const preset = d.preset ? DEVICE_PRESETS[d.preset as DevicePreset] : undefined;
    return {
      width: d.width ?? preset!.width,
      height: d.height ?? preset!.height,
    };
  });

// YAML lets users write `monthly:` with nothing under it, which parses as null.
// Treat null/undefined as an empty object so .default() on inner fields applies.
const obj = <T extends z.ZodRawShape>(shape: T) =>
  z.preprocess((v) => v ?? {}, z.object(shape));

export const configSchema = z.preprocess(
  (v) => v ?? {},
  z.object({
    mode: z.enum(['life', 'yearly', 'monthly']).default('life'),
    device: deviceSchema.default({ preset: 'iphone-15-pro-max' } as never),

    life: obj({
      birthDate: isoDate.default('1995-01-01'),
      lifeExpectancy: z.number().int().min(1).max(150).default(80),
      unit: z.enum(['days', 'weeks', 'months']).default('weeks'),
      grouping: z.enum(['none', 'decade']).default('decade'),
    }).default({} as never),

    yearly: obj({
      year: z.number().int().min(1900).max(3000).optional(),
      weekStartDay: z.enum(['monday', 'sunday']).default('monday'),
      weeksPerRow: z.union([z.literal(1), z.literal(2)]).default(2),
    }).default({} as never),

    monthly: obj({
      year: z.number().int().min(1900).max(3000).optional(),
      month: z.number().int().min(1).max(12).optional(),
      weekStartDay: z.enum(['monday', 'sunday']).default('monday'),
    }).default({} as never),

    shape: z.enum(['circle', 'square']).default('circle'),
    showTitle: z.boolean().default(true),
    title: z.string().default('MEMENTO MORI'),
    showLabels: z.boolean().default(true),
    highlightToday: z.boolean().default(true),

    padding: obj({
      horizontal: z.number().nonnegative().default(60),
      vertical: z.number().nonnegative().default(60),
      clockOffset: z.union([z.number().nonnegative(), z.literal('auto')]).default(0),
      bottomOffset: z.union([z.number().nonnegative(), z.literal('auto')]).default(0),
    }).default({} as never),

    colors: z.preprocess(
      (v) => v ?? {},
      z.object({
        preset: z.string().optional(),
        background: hexColor.optional(),
        filled: hexColor.optional(),
        empty: hexColor.optional(),
        today: hexColor.optional(),
        text: hexColor.optional(),
      })
        .superRefine((c, ctx) => {
          if (c.preset && !(c.preset in COLOR_PRESETS)) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ['preset'],
              message: `unknown preset; valid: ${Object.keys(COLOR_PRESETS).join(', ')}`,
            });
          }
        })
        .transform((c) => {
          const p = COLOR_PRESETS[(c.preset as ColorPreset) ?? 'dark'] ?? COLOR_PRESETS.dark;
          return {
            background: c.background ?? p.background,
            filled: c.filled ?? p.filled,
            empty: c.empty ?? p.empty,
            today: c.today ?? p.today,
            text: c.text ?? p.text,
          };
        }),
    ).default({} as never),
  }),
);

export type WallpaperConfig = z.infer<typeof configSchema>;
export type RawConfig = z.input<typeof configSchema>;
